import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from '@pesto/ui';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { Switch } from '@pesto/ui/components/Switch';
import { Settings } from 'lucide-react';
import { useEffect, useState } from 'react';

import { EntityHeader } from '../../EntityProfile/components/EntityHeader';

export interface AutopaySettingsValues {
  enabled: boolean;
  paymentType: string;
  daysBeforePayment: number;
  plaidAccountId: string;
}

type AutopaySettingsProps = {
  settings: AutopaySettingsValues | null | undefined;
  loading?: boolean;
} & (
  | { readOnly: true; onSave?: never; saving?: never }
  | { readOnly?: false; onSave: (next: AutopaySettingsValues) => void | Promise<void>; saving?: boolean }
);

export const AUTOPAY_PAYMENT_TYPE_OPTIONS = [
  { value: 'minimum', label: 'Minimum Payment' },
  { value: 'statement', label: 'Statement Balance' },
  { value: 'full', label: 'Current Balance' },
];

const getDaySuffix = (day: number) => {
  if (day > 3 && day < 21) return 'th';

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const DAYS_BEFORE_PAYMENT_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `${i + 1}${getDaySuffix(i + 1)}`,
}));

export const AUTOPAY_SETTINGS_DEFAULTS: AutopaySettingsValues = {
  enabled: false,
  paymentType: 'minimum',
  daysBeforePayment: 1,
  plaidAccountId: '',
};

export const AutopaySettings = (props: AutopaySettingsProps) => {
  const { settings, loading, readOnly } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<AutopaySettingsValues>(settings ?? AUTOPAY_SETTINGS_DEFAULTS);

  useEffect(() => {
    if (settings) setDraft(settings);
  }, [settings]);

  const handleSave = async () => {
    if (readOnly) return;

    try {
      await props.onSave({
        ...draft,
        plaidAccountId: draft.plaidAccountId || 'placeholder-id',
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving autopay settings', error);
    }
  };

  const handleCancel = () => {
    setDraft(settings ?? AUTOPAY_SETTINGS_DEFAULTS);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex h-32 items-center justify-center">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <EntityHeader
        config={{
          avatar: { icon: Settings },
          title: 'Autopay Settings',
          description: 'Manage automatic payment preferences',
        }}
      />

      <CardContent className="space-y-6">
        {isEditing ? (
          <>
            <div className="bg-muted/50 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Typography variant="body" className="font-medium">
                    Enable Autopay
                  </Typography>
                  <Typography variant="body" className="text-muted-foreground text-sm">
                    Automatically pay your credit card balance
                  </Typography>
                </div>
                <Switch checked={draft.enabled} onCheckedChange={(enabled) => setDraft((d) => ({ ...d, enabled }))} />
              </div>
            </div>

            {draft.enabled && (
              <div className="space-y-4">
                <Typography
                  variant="body"
                  className="text-muted-foreground text-sm font-semibold uppercase tracking-wide"
                >
                  Payment Configuration
                </Typography>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Typography variant="body" className="text-sm font-medium">
                      Payment Type
                    </Typography>
                    <Select
                      value={draft.paymentType}
                      onValueChange={(paymentType) => setDraft((d) => ({ ...d, paymentType }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {AUTOPAY_PAYMENT_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Typography variant="body" className="text-sm font-medium">
                      Days Before Due Date
                    </Typography>
                    <Select
                      value={draft.daysBeforePayment.toString()}
                      onValueChange={(value) => setDraft((d) => ({ ...d, daysBeforePayment: Number(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select days" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_BEFORE_PAYMENT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 border-t pt-4">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                disabled={readOnly ? false : props.saving}
              >
                {!readOnly && props.saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-muted/50 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Typography variant="body" className="font-medium">
                    Autopay Status
                  </Typography>
                  <Typography variant="body" className="text-muted-foreground text-sm">
                    {settings?.enabled ? 'Automatic payments are active' : 'Automatic payments are disabled'}
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${settings?.enabled ? 'bg-primary' : 'bg-muted-foreground'}`} />
                  <Typography variant="body" className="text-sm font-medium">
                    {settings?.enabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </div>
              </div>
            </div>

            {settings?.enabled && (
              <div className="space-y-4">
                <Typography
                  variant="body"
                  className="text-muted-foreground text-sm font-semibold uppercase tracking-wide"
                >
                  Current Settings
                </Typography>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="bg-muted/20 rounded-lg border p-3">
                    <div className="space-y-1">
                      <Typography
                        variant="body"
                        className="text-muted-foreground text-xs font-medium uppercase tracking-wide"
                      >
                        Payment Type
                      </Typography>
                      <Typography variant="body" className="text-sm font-semibold">
                        {AUTOPAY_PAYMENT_TYPE_OPTIONS.find((option) => option.value === settings.paymentType)?.label ||
                          'Minimum Payment'}
                      </Typography>
                    </div>
                  </div>

                  <div className="bg-muted/20 rounded-lg border p-3">
                    <div className="space-y-1">
                      <Typography
                        variant="body"
                        className="text-muted-foreground text-xs font-medium uppercase tracking-wide"
                      >
                        Payment Timing
                      </Typography>
                      <Typography variant="body" className="text-sm font-semibold">
                        {settings.daysBeforePayment}
                        {getDaySuffix(settings.daysBeforePayment || 1)} day before due date
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!readOnly && (
              <div className="border-t pt-4">
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  {settings?.enabled ? 'Edit Settings' : 'Set Up Autopay'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
